import React, { useState, useRef, useCallback } from 'react';
import { useHistory } from 'react-router-dom';

import Grid from '@mui/material/Grid';
import DataTable from '../dataTable/DataTable/DataTable';
import { IDataTableColumn } from '../dataTable/DataTable/model';

interface InteractiveDataTableProps {
  clickableRows?: boolean;
  controller: string;
  columns: any;
  loading: boolean;
  expandableRowsComponent: any;
  fetchRecordsList: any;
  filterValues: any;
  perPage?: number;
  records: any;
  onRowClick?: (row: any) => void;
  rowClickCondition?: (row: any) => boolean;
  rowClickPath?: string;
  rowClickQueryString?: string;
  searchQuery: string;
  selectableRows: any;
  sortParam: any;
  setSortParam: any;
  totalRecords: any;
}

const InteractiveDataTable: React.FunctionComponent<InteractiveDataTableProps> = (props) => {
  const history = useHistory();

  const {
    clickableRows,
    controller,
    columns,
    loading,
    expandableRowsComponent,
    fetchRecordsList,
    filterValues,
    onRowClick,
    perPage = 25,
    records,
    rowClickCondition,
    rowClickPath,
    rowClickQueryString,
    searchQuery,
    selectableRows,
    sortParam,
    setSortParam,
    totalRecords,
  } = props;

  const [selectedRecords, setSelectedRecords] = useState<any[]>([]);

  // Flags to ignore the first automatic call from DataTable
  const pageInitRef = useRef(false);
  const sortInitRef = useRef(false);

  const handleColumnSort = useCallback(
    (column: IDataTableColumn<Record<any, any>>, sortDirection: string) => {
      // DataTable will call this on mount / when props change.
      // Skip that first call and only handle real user interactions.
      if (!sortInitRef.current) {
        sortInitRef.current = true;
        return;
      }

      const newSortParam = `${column.selector} ${sortDirection}`;
      setSortParam(newSortParam);
      fetchRecordsList(controller, filterValues, 1, searchQuery, newSortParam);
    },
    [controller, filterValues, fetchRecordsList, searchQuery, setSortParam],
  );

  const handlePageChange = useCallback(
    (newPage: number) => {
      // Same idea: ignore the first automatic call.
      if (!pageInitRef.current) {
        pageInitRef.current = true;
        return;
      }
      fetchRecordsList(controller, filterValues, newPage, searchQuery, sortParam);
    },
    [controller, filterValues, fetchRecordsList, searchQuery, sortParam],
  );

  const handleRowClicked = (row: any) => {
    if (!clickableRows) return;
    if (selectableRows) return; // for now, keep this restriction
    if (onRowClick) {
      onRowClick(row);
      return;
    }
    if (rowClickCondition && !rowClickCondition(row)) return;
    const rootToUse = rowClickPath || controller;
    const qs = rowClickQueryString ? `?${rowClickQueryString}` : '';
    history.push(`/${rootToUse}/${row.id}${qs}`);
  };

  const showExpandable = !!expandableRowsComponent;

  const handleSelectedRows = ({ selectedRows }) => {
    setSelectedRecords(selectedRows);
  };

  const rowSelectCriteria = (row) => selectedRecords?.includes(row);

  return (
    <Grid container>
      <Grid item xs={12}>
        <DataTable
          onRowClicked={handleRowClicked}
          columns={columns}
          data={records}
          progressPending={loading}
          striped
          highlightOnHover
          pointerOnHover={clickableRows}
          pagination
          paginationPerPage={perPage}
          paginationRowsPerPageOptions={[25]}
          paginationServer
          paginationTotalRows={totalRecords}
          onChangePage={handlePageChange}
          noHeader
          sortServer
          onSort={handleColumnSort}
          expandableRows={showExpandable}
          expandableRowsComponent={showExpandable ? expandableRowsComponent : <></>}
          selectableRows={selectableRows}
          onSelectedRowsChange={handleSelectedRows}
          selectableRowSelected={rowSelectCriteria}
        />
      </Grid>
    </Grid>
  );
};

export default InteractiveDataTable;
